import React, { useState, useRef, useCallback, useEffect } from "react";
import ReactCrop, { type Crop, type PixelCrop, centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

interface ImageEditorProps {
  imageUrl: string;
  imageName: string;
  onSave: (editedDataUrl: string) => void;
  onCancel: () => void;
}

interface TransformState {
  rotation: number; // 0, 90, 180, 270
  flipH: boolean;
  flipV: boolean;
  scale: number;
}

const ImageEditor: React.FC<ImageEditorProps> = ({ imageUrl, imageName, onSave, onCancel }) => {
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [transform, setTransform] = useState<TransformState>({
    rotation: 0,
    flipH: false,
    flipV: false,
    scale: 100,
  });
  const [imgDimensions, setImgDimensions] = useState({ width: 0, height: 0 });
  const [previewUrl, setPreviewUrl] = useState(imageUrl);
  const imgRef = useRef<HTMLImageElement>(null);
  const sourceImageRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    setCrop(undefined);
    setCompletedCrop(undefined);
  }, [imageUrl]);

  // Set crop to select all
  const selectAll = useCallback(() => {
    if (!imgRef.current) return;
    const { width, height } = imgRef.current;
    setCrop({
      unit: "px",
      x: 0,
      y: 0,
      width,
      height,
    });
  }, []);

  // Apply crop as center square
  const cropSquare = useCallback(() => {
    if (!imgRef.current) return;
    const { width, height } = imgRef.current;
    const size = Math.min(width, height);
    const crop = centerCrop(
      makeAspectCrop({ unit: "px", width: size }, 1, width, height),
      width,
      height
    );
    setCrop(crop);
  }, []);

  // Rotate image
  const rotate = useCallback((degrees: number) => {
    setTransform((prev) => ({
      ...prev,
      rotation: ((prev.rotation + degrees + 360) % 360) as 0 | 90 | 180 | 270,
    }));
    // Clear crop when rotating
    setCrop(undefined);
    setCompletedCrop(undefined);
  }, []);

  // Flip image
  const flip = useCallback((direction: "h" | "v") => {
    setTransform((prev) => ({
      ...prev,
      flipH: direction === "h" ? !prev.flipH : prev.flipH,
      flipV: direction === "v" ? !prev.flipV : prev.flipV,
    }));
  }, []);

  // Reset all transforms
  const resetAll = useCallback(() => {
    setTransform({ rotation: 0, flipH: false, flipV: false, scale: 100 });
    setCrop(undefined);
    setCompletedCrop(undefined);
  }, []);

  const renderPreview = useCallback(() => {
    const image = sourceImageRef.current;
    if (!image) return;
    if (!image.naturalWidth || !image.naturalHeight) return;

    const scale = Math.max(1, transform.scale) / 100;
    const outW = Math.max(1, Math.round(image.naturalWidth * scale));
    const outH = Math.max(1, Math.round(image.naturalHeight * scale));
    const swapDims = transform.rotation === 90 || transform.rotation === 270;

    const canvas = document.createElement("canvas");
    canvas.width = swapDims ? outH : outW;
    canvas.height = swapDims ? outW : outH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.imageSmoothingEnabled = transform.scale >= 100;
    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((transform.rotation * Math.PI) / 180);
    ctx.scale(transform.flipH ? -1 : 1, transform.flipV ? -1 : 1);
    ctx.drawImage(
      image,
      0,
      0,
      image.naturalWidth,
      image.naturalHeight,
      -outW / 2,
      -outH / 2,
      outW,
      outH
    );
    ctx.restore();

    setPreviewUrl(canvas.toDataURL("image/png"));
  }, [transform]);

  useEffect(() => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      sourceImageRef.current = image;
      setImgDimensions({ width: image.naturalWidth, height: image.naturalHeight });
      renderPreview();
    };
    image.src = imageUrl;
  }, [imageUrl, renderPreview]);

  useEffect(() => {
    renderPreview();
  }, [renderPreview]);

  // Generate the edited image and save
  const handleSave = useCallback(() => {
    const image = imgRef.current;
    if (!image) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d")!;
    ctx.imageSmoothingEnabled = transform.scale >= 100;

    // Source dimensions (from crop or full image)
    let srcX = 0;
    let srcY = 0;
    let srcW = image.naturalWidth;
    let srcH = image.naturalHeight;

    if (completedCrop && completedCrop.width && completedCrop.height) {
      // Calculate scale factor between displayed image and natural size
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      srcX = completedCrop.x * scaleX;
      srcY = completedCrop.y * scaleY;
      srcW = completedCrop.width * scaleX;
      srcH = completedCrop.height * scaleY;
    }

    canvas.width = Math.max(1, Math.round(srcW));
    canvas.height = Math.max(1, Math.round(srcH));
    ctx.drawImage(image, srcX, srcY, srcW, srcH, 0, 0, canvas.width, canvas.height);

    // Export as PNG to preserve quality
    const editedDataUrl = canvas.toDataURL("image/png");
    onSave(editedDataUrl);
  }, [completedCrop, transform, onSave]);

  // Calculate preview transform style
  const previewStyle: React.CSSProperties = {
    imageRendering: transform.scale < 100 ? "pixelated" : "auto",
  };

  // Calculate container style for rotated images
  const containerStyle: React.CSSProperties = {
    maxWidth: "100%",
    maxHeight: "60vh",
  };

  const previewElement = imgRef.current;
  const previewScaleX = previewElement?.width
    ? previewElement.naturalWidth / previewElement.width
    : 1;
  const previewScaleY = previewElement?.height
    ? previewElement.naturalHeight / previewElement.height
    : 1;
  const outputWidth = previewElement
    ? Math.round((completedCrop?.width || previewElement.width) * previewScaleX)
    : 0;
  const outputHeight = previewElement
    ? Math.round((completedCrop?.height || previewElement.height) * previewScaleY)
    : 0;

  return (
    <div className="image-editor-overlay" onClick={onCancel}>
      <div className="image-editor-modal" onClick={(e) => e.stopPropagation()}>
        <div className="editor-header">
          <h3>Edit Image</h3>
          <span className="editor-filename">{imageName}</span>
          <button className="editor-close-btn" onClick={onCancel}>
            &times;
          </button>
        </div>

        <div className="editor-canvas-container">
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
            style={containerStyle}
          >
            <img
              ref={imgRef}
              src={previewUrl}
              alt="Edit"
              style={{
                ...previewStyle,
                width: "100%",
                height: "auto",
                maxHeight: "60vh",
                display: "block",
              }}
              crossOrigin="anonymous"
            />
          </ReactCrop>
        </div>

        <div className="editor-toolbar">
          {/* Crop tools */}
          <div className="toolbar-group">
            <span className="toolbar-label">Crop</span>
            <button
              className="toolbar-btn"
              onClick={() => {
                setCrop(undefined);
                setCompletedCrop(undefined);
              }}
              title="Clear crop selection"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
            <button className="toolbar-btn" onClick={selectAll} title="Select all">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" />
              </svg>
            </button>
            <button className="toolbar-btn" onClick={cropSquare} title="Square crop">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="6" y="6" width="12" height="12" />
              </svg>
            </button>
          </div>

          {/* Rotate tools */}
          <div className="toolbar-group">
            <span className="toolbar-label">Rotate</span>
            <button className="toolbar-btn" onClick={() => rotate(-90)} title="Rotate left">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M2.5 2v6h6M2.66 15.57a10 10 0 1 0 .57-8.38" />
              </svg>
            </button>
            <button className="toolbar-btn" onClick={() => rotate(90)} title="Rotate right">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: "scaleX(-1)" }}>
                <path d="M2.5 2v6h6M2.66 15.57a10 10 0 1 0 .57-8.38" />
              </svg>
            </button>
          </div>

          {/* Flip tools */}
          <div className="toolbar-group">
            <span className="toolbar-label">Flip</span>
            <button
              className={`toolbar-btn ${transform.flipH ? "active" : ""}`}
              onClick={() => flip("h")}
              title="Flip horizontal"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 3v18M16 7l4 5-4 5M8 7L4 12l4 5" />
              </svg>
            </button>
            <button
              className={`toolbar-btn ${transform.flipV ? "active" : ""}`}
              onClick={() => flip("v")}
              title="Flip vertical"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 12h18M7 8L12 4l5 4M7 16l5 4 5-4" />
              </svg>
            </button>
          </div>

          {/* Scale/Resize */}
          <div className="toolbar-group">
            <span className="toolbar-label">Scale</span>
            <input
              type="range"
              className="scale-slider"
              min="10"
              max="200"
              value={transform.scale}
              onChange={(e) =>
                setTransform((prev) => ({ ...prev, scale: parseInt(e.target.value) }))
              }
            />
            <span className="scale-value">{transform.scale}%</span>
          </div>

          {/* Reset */}
          <div className="toolbar-group">
            <button className="toolbar-btn reset-btn" onClick={resetAll} title="Reset all">
              Reset
            </button>
          </div>
        </div>

        {/* Info bar */}
        <div className="editor-info">
          {imgDimensions.width > 0 && (
            <span>
              Original: {imgDimensions.width} × {imgDimensions.height}px
              {outputWidth > 0 && outputHeight > 0 && (
                <>{" → "}Output: {outputWidth} × {outputHeight}px</>
              )}
            </span>
          )}
        </div>

        <div className="editor-actions">
          <button className="editor-cancel-btn" onClick={onCancel}>
            Cancel
          </button>
          <button className="editor-save-btn" onClick={handleSave}>
            Apply Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageEditor;
